import { useState } from 'react';
import { fetchOpenAIText } from '../utils/openaiClient';
import { addWord, getDictionary } from '../utils/dictionaryService';
import { supabase } from '../utils/supabaseClient';

// Extracted from LessonWorkspace.js (Phase 3 of the platform-health plan:
// getting large files under a saner line count needs pulling logic into
// hooks, not just JSX into components). Owns the "select text → Add to
// Dictionary" flow end to end: looking up part-of-speech variants via
// OpenAI, the variants-picker modal's own state, and actually writing the
// chosen word(s) to the student's dictionary.
export function useDictionaryLookup({ studentId, contextMenu, setContextMenu, showLessonSaveToast, dismissLessonSaveToast }) {
  const [dictWordVariants, setDictWordVariants] = useState([]);
  const [showDictVariantsModal, setShowDictVariantsModal] = useState(false);
  const [dictPendingWord, setDictPendingWord] = useState('');
  const [dictSelectedIndices, setDictSelectedIndices] = useState(new Set());
  const [dictCurrentEntries, setDictCurrentEntries] = useState([]);
  const [dictResolvedStudentId, setDictResolvedStudentId] = useState(null);

  const dictNormalizePos = (pos) => {
    if (!pos) return null;
    const p = pos.toLowerCase().trim();
    if (p.includes('phrasal')) return 'phrasal verb';
    if (p.includes('idiom')) return 'idiom';
    if (p.includes('noun') || p.includes('substantive')) return 'noun';
    if (p.includes('verb')) return 'verb';
    if (p.includes('adjective') || p === 'adj') return 'adjective';
    if (p.includes('adverb') || p === 'adv') return 'adverb';
    if (p.includes('pronoun')) return 'pronoun';
    if (p.includes('preposition') || p === 'prep') return 'preposition';
    if (p.includes('conjunction') || p === 'conj') return 'conjunction';
    if (p.includes('interjection') || p.includes('exclamation')) return 'interjection';
    return p;
  };

  const dictTranslationWords = (text) => {
    if (!text) return new Set();
    return new Set(
      text.toLowerCase()
        .replace(/[()[\],.;:!?–—-]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 2)
    );
  };

  const dictTranslationSimilarity = (a, b) => {
    const wa = dictTranslationWords(a);
    const wb = dictTranslationWords(b);
    if (!wa.size && !wb.size) return 1;
    if (!wa.size || !wb.size) return 0;
    const intersection = [...wa].filter(w => wb.has(w)).length;
    const union = new Set([...wa, ...wb]).size;
    return intersection / union;
  };

  const isDictVariantAlreadyAdded = (variant, entries, pendingWord) => {
    const word = pendingWord.toLowerCase();
    const variantPos = dictNormalizePos(variant.partOfSpeech);
    const variantType = variant.type || 'regular';
    return entries.some(entry => {
      if (entry.word?.toLowerCase() !== word) return false;
      const entryPos = dictNormalizePos(entry.partOfSpeech);
      if (entryPos && variantPos && entryPos === variantPos) return true;
      if ((entry.type || 'regular') === variantType && variantType !== 'regular') return true;
      if (dictTranslationSimilarity(entry.translation, variant.translation) >= 0.3) return true;
      return false;
    });
  };

  const resolveStudentUuidForDict = async (id) => {
    if (!id) return null;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (isUUID) return id;
    try {
      const { data } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', id)
        .maybeSingle();
      return data?.id || null;
    } catch {
      return null;
    }
  };

  const closeDictVariantsModal = () => {
    setShowDictVariantsModal(false);
    setDictWordVariants([]);
    setDictPendingWord('');
    setDictSelectedIndices(new Set());
    setDictCurrentEntries([]);
  };

  const toggleDictVariantIndex = (idx) => {
    setDictSelectedIndices(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const addDictWordVariant = async (variant, word, resolvedId) => {
    await addWord(resolvedId, {
      word: word,
      translation: variant.translation || 'Translation not available',
      level: variant.level || 'B1',
      example: variant.example || null,
      type: variant.type || 'regular',
      partOfSpeech: variant.partOfSpeech || null,
    });
    window.dispatchEvent(new CustomEvent('dictionaryUpdated', { detail: { studentId: resolvedId } }));
  };

  const handleDictSelectSelected = async () => {
    const resolvedId = dictResolvedStudentId;
    const word = dictPendingWord;
    const toAdd = dictWordVariants.filter((_, i) => dictSelectedIndices.has(i));
    for (const variant of toAdd) {
      await addDictWordVariant(variant, word, resolvedId);
    }
    // Refresh entries for "already added" detection in case modal stays open
    const entries = await getDictionary(resolvedId);
    setDictCurrentEntries(Array.isArray(entries) ? entries : []);
    closeDictVariantsModal();
    showLessonSaveToast('success', `Added ${toAdd.length} word${toAdd.length !== 1 ? 's' : ''} to dictionary!`, 2500);
  };

  const handleDictSelectAll = async () => {
    const resolvedId = dictResolvedStudentId;
    const word = dictPendingWord;
    for (const variant of dictWordVariants) {
      await addDictWordVariant(variant, word, resolvedId);
    }
    closeDictVariantsModal();
    showLessonSaveToast('success', `Added ${dictWordVariants.length} word${dictWordVariants.length !== 1 ? 's' : ''} to dictionary!`, 2500);
  };
  // --- end dictionary variants modal helpers ---

  const handleAddToDictionary = async () => {
    if (!contextMenu.selectedText || !studentId) return;

    const word = contextMenu.selectedText.trim();
    setContextMenu({ show: false, x: 0, y: 0, selectedText: '', showText: false });
    showLessonSaveToast('info', 'Looking up word variants…', 6000);

    try {
      const resolvedId = await resolveStudentUuidForDict(studentId);
      if (!resolvedId) {
        showLessonSaveToast('error', 'Error: Student ID not found', 2500);
        return;
      }

      // Load current dictionary entries for "already added" detection
      let currentEntries = [];
      try {
        const entries = await getDictionary(resolvedId);
        currentEntries = Array.isArray(entries) ? entries : [];
      } catch { /* ignore */ }

      const variantsPrompt = `List all possible parts of speech for the word or phrase "${word}". For each meaning, determine:
1. Part of speech (noun, verb, adjective, adverb, phrasal verb, idiom, etc.)
2. Type: if it's a phrasal verb or idiom, indicate this in the "type" field
3. CEFR level (A1, A2, B1, B2, C1, or C2)
4. Russian translation - IMPORTANT: include ALL meanings and translation variants with detailed context in parentheses, do NOT shorten translations
5. Example sentence in English (8-15 words)

Respond ONLY with valid JSON in format:
{
  "variants": [
    {
      "partOfSpeech": "noun",
      "type": "regular",
      "level": "A1",
      "translation": "work (process of labor, occupation, activity)",
      "example": "I have a lot of work to do."
    }
  ]
}`;

      const content = await fetchOpenAIText({
        prompt: variantsPrompt,
        temperature: 0.7,
        maxTokens: 800,
      });

      dismissLessonSaveToast();

      if (!content) throw new Error('No response from OpenAI API');

      let jsonStr = content.trim();
      if (jsonStr.startsWith('```json')) {
        jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
      } else if (jsonStr.startsWith('```')) {
        jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
      }

      const match = jsonStr.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        if (parsed.variants && Array.isArray(parsed.variants) && parsed.variants.length > 0) {
          if (parsed.variants.length === 1) {
            // Single variant — add directly
            await addDictWordVariant(parsed.variants[0], word, resolvedId);
            showLessonSaveToast('success', `"${word}" added to dictionary!`, 2500);
          } else {
            // Multiple variants — show selection modal
            setDictResolvedStudentId(resolvedId);
            setDictCurrentEntries(currentEntries);
            setDictPendingWord(word);
            setDictWordVariants(parsed.variants);
            setDictSelectedIndices(new Set());
            setShowDictVariantsModal(true);
          }
          return;
        }
      }

      // Fallback: add single word directly if parsing fails
      await addWord(resolvedId, { word, translation: 'Translation not available', level: 'B1', type: 'regular' });
      window.dispatchEvent(new CustomEvent('dictionaryUpdated', { detail: { studentId: resolvedId } }));
      showLessonSaveToast('success', `"${word}" added to dictionary!`, 2500);
    } catch (error) {
      console.error('Error adding word to dictionary:', error);
      showLessonSaveToast('error', 'Error adding word to dictionary', 2500);
    }
  };

  return {
    dictWordVariants,
    showDictVariantsModal,
    dictPendingWord,
    dictSelectedIndices,
    dictCurrentEntries,
    isDictVariantAlreadyAdded,
    closeDictVariantsModal,
    toggleDictVariantIndex,
    handleDictSelectSelected,
    handleDictSelectAll,
    handleAddToDictionary,
  };
}
